import AFG from '../assets/img/flags/AFG-Flag.png';
import USA from '../assets/img/flags/USA-Flag.png';
import TR from '../assets/img/flags/TR-Flag.png';
import AR from '../assets/img/flags/AR-Flag.png';
import EL from '../assets/img/flags/EL-Flag.png';
import RU from '../assets/img/flags/RU-Flag.png';

export const SUPPORTED_LANGUAGES = ['en', 'tr', 'fa', 'ar', 'el', 'ru', 'ps'];

/** 'en-US' / ' TR ' -> 'en' / 'tr'. Returns '' for anything that isn't a language tag. */
export function normalizeLanguageCode(value) {
    const base = String(value || '').trim().toLowerCase().split(/[-_]/)[0];
    return /^[a-z]{2}$/.test(base) ? base : '';
}

/**
 * The languages a given store offers, from its `supportedLocales` setting.
 * Merchant order is preserved (the first entry is their default), and codes the
 * storefront has no bundled translation for are dropped — offering them would
 * show a half-translated page. Falls back to every bundled language when the
 * store has no usable selection.
 */
export function resolveStoreLanguages(supportedLocales) {
    if (!Array.isArray(supportedLocales)) return SUPPORTED_LANGUAGES;

    const seen = new Set();
    const resolved = [];
    for (const locale of supportedLocales) {
        const code = normalizeLanguageCode(locale);
        if (!code || seen.has(code) || !SUPPORTED_LANGUAGES.includes(code)) continue;
        seen.add(code);
        resolved.push(code);
    }

    return resolved.length ? resolved : SUPPORTED_LANGUAGES;
}

/** Matches /en or /s/org/store/en at the start of a path. */
export const LANG_CODE_PATTERN = /^(?:\/s\/[^/]+\/[^/]+)?\/([a-z]{2})(\/|$)/;

export function replacePathLanguage(pathname, language) {
    const path = String(pathname || '/') || '/';
    const storeMatch = path.match(/^(\/s\/[^/]+\/[^/]+)(?=\/|$)/i);
    const prefix = storeMatch ? storeMatch[1] : '';
    const rest = prefix ? path.slice(prefix.length) || '/' : path;

    let newRest;
    if (/^\/[a-z]{2}(?=\/|$)/i.test(rest)) {
        newRest = rest.replace(/^\/[a-z]{2}(?=\/|$)/i, `/${language}`);
    } else if (!rest || rest === '/') {
        newRest = `/${language}`;
    } else {
        newRest = `/${language}${rest.startsWith('/') ? rest : `/${rest}`}`;
    }

    return `${prefix}${newRest}`.replace(/\/{2,}/g, '/') || `/${language}`;
}

function safeDecode(value) {
    try {
        return decodeURIComponent(value);
    } catch {
        return value;
    }
}

/** Splits the /s/org/store and /:lang prefixes off a pathname for analytics grouping. */
export function normalizeAnalyticsPath(pathname) {
    const raw = String(pathname || '/') || '/';
    const storeMatch = raw.match(/^(\/s\/([^/]+)\/([^/]+))(?=\/|$)/i);
    let rest = storeMatch ? raw.slice(storeMatch[1].length) : raw;

    let language = null;
    // LANG_CODE_PATTERN accepts any two letters, which would turn /menu into /nu.
    // Only a code the app actually serves may be stripped.
    const langMatch = rest.match(/^\/([a-z]{2})(?=\/|$)/i);
    if (langMatch && SUPPORTED_LANGUAGES.includes(langMatch[1].toLowerCase())) {
        language = langMatch[1].toLowerCase();
        rest = rest.slice(langMatch[0].length);
    }

    let path = String(rest || '/').replace(/\/{2,}/g, '/');
    if (!path.startsWith('/')) path = `/${path}`;
    if (path.length > 1) path = path.replace(/\/+$/, '');
    if (!path) path = '/';

    return {
        path: safeDecode(path),
        language,
        orgSlug: storeMatch ? safeDecode(storeMatch[2]) : null,
        storeSlug: storeMatch ? safeDecode(storeMatch[3]) : null,
    };
}

export const LANGUAGE_FLAGS = {
    en: USA,
    tr: TR,
    fa: AFG,
    ar: AR,
    el: EL,
    ru: RU,
    ps: AFG,
};

export const LANGUAGE_NAMES = {
    en: 'English',
    tr: 'Türkçe',
    fa: 'فارسی',
    ar: 'العربية',
    el: 'Ελληνικά',
    ru: 'Русский',
    ps: 'پښتو',
};
